import { useAtomValue } from 'jotai';
import { type CSSProperties, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box } from '@optiaxiom/react';

import { opalOverlayOpenAtom } from '@frontend/atoms';
import {
  stickyboardDrawerVisibleAtom,
  stickyboardDrawerWidthAtom,
} from '@frontend/atoms/stickyboardAtoms';
import { AssumedVAUBanner } from '@frontend/components/AssumedVAUBanner';
import { IdentitySwitchScrim } from '@frontend/components/IdentitySwitchScrim';
import { SidebarSlotContext, useSlotNode } from '@frontend/components/sidebar/sidebarSlot';
import { Spotlight } from '@frontend/components/Spotlight';
import { SpotlightGlobalCommands } from '@frontend/components/Spotlight/commands';
import { SpotlightProvider } from '@frontend/components/Spotlight/store';
import { OVERLAY_PAGE_SEGMENTS } from '@frontend/constants';
import { RUNTIME_ENV } from '@frontend/envConfig';
import { OpalChatOverlay } from '@frontend/features/chat/components/OpalChatOverlay';
import { useDesktopQuickAsk } from '@frontend/features/chat/hooks/useDesktopQuickAsk';
import { useDesktopAccountBar } from '@frontend/features/instancePicker/hooks/useDesktopAccountBar';
import { useDesktopInstanceBar } from '@frontend/features/instancePicker/hooks/useDesktopInstanceBar';
import { LeftNavbar } from '@frontend/features/leftNavbar';
import {
  useDesktopBadge,
  useNotificationFavicon,
  useNotificationTitle,
} from '@frontend/features/notifications/hooks';
import { StickyboardDrawer } from '@frontend/features/stickyboard/components/StickyboardDrawer';
import { useRouteDocumentTitle } from '@frontend/hooks/useDocumentTitle';
import { useFullstoryPageContext } from '@frontend/hooks/useFullstoryPageContext';
import { useNotificationEvents } from '@frontend/hooks/useNotificationEvents';
import { useOpalUser } from '@frontend/hooks/useOpalUser';
import { SIDEBAR_WIDTH } from '@frontend/styles/layout.css';

import {
  baseLayoutStyles,
  pageColumnStyles,
  scrollAreaStyles,
  stageStyles,
} from './BaseLayout.css';
import { RightSidebarHost } from './RightSidebarHost';

export function BaseLayout() {
  const { opalUser } = useOpalUser();
  useRouteDocumentTitle();
  useFullstoryPageContext();
  useNotificationEvents();
  useNotificationFavicon();
  useDesktopBadge();
  useDesktopInstanceBar();
  useDesktopQuickAsk();
  const { isSwitchingIdentity, switchingMessage } = useDesktopAccountBar();
  useNotificationTitle();

  useEffect(() => {
    if (!opalUser) {
      return;
    }

    if (RUNTIME_ENV === 'production' || RUNTIME_ENV === 'integration') {
      void import(/* webpackChunkName: "Userflow" */ '@frontend/userflow').then(
        ({ default: Userflow }) => {
          Userflow.init(opalUser, opalUser.userflow_token);
        }
      );
    }
  }, [opalUser]);

  const { node: stageNode, ref: stageRef } = useSlotNode();

  // When the Tracker drawer is docked ("held"), reserve space for it. The
  // drawer is right-anchored, so the scroll area gets a right margin equal to
  // the drawer width (narrowing it from the right). The Tracker drawer is
  // always docked since the 2026-07-24 review removed float mode.
  const drawerVisible = useAtomValue(stickyboardDrawerVisibleAtom);
  const drawerWidth = useAtomValue(stickyboardDrawerWidthAtom);
  const chatOpen = useAtomValue(opalOverlayOpenAtom);
  const { pathname } = useLocation();
  const heldMargin = drawerVisible ? drawerWidth : 0;
  // When the Ask Opal overlay is also on screen, the docked Tracker is shifted
  // left by SIDEBAR_WIDTH (see StickyboardDrawer) so both trays are visible —
  // reserve that extra width too so docked content clears BOTH. Gated on the
  // Tracker being docked so the overlay-only case keeps its existing (no-reflow)
  // behavior, and mirrors the overlay's own visibility gate.
  const segments = pathname.split('/');
  const chatOverlayShowing =
    chatOpen && !segments.includes('chat') && OVERLAY_PAGE_SEGMENTS.some(s => segments.includes(s));
  const contentMarginRight = heldMargin + (heldMargin && chatOverlayShowing ? SIDEBAR_WIDTH : 0);

  // Measure the content stage so the Stickyboard drawer can render as a fixed
  // overlay aligned to it — outside the stage, above the chat MFE + chat
  // overlay (which the stage's `isolation` would otherwise trap it beneath).
  // Only observe when the drawer is visible — no point measuring otherwise.
  const [stageRect, setStageRect] = useState({ left: 0, top: 0, height: 0 });
  useEffect(() => {
    if (!stageNode || !drawerVisible) return;
    const update = () => {
      const r = stageNode.getBoundingClientRect();
      setStageRect({ left: r.left, top: r.top, height: r.height });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(stageNode);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [stageNode, drawerVisible]);

  return (
    <SpotlightProvider>
      <SidebarSlotContext.Provider value={stageNode}>
        <Box className={pageColumnStyles}>
          {/* Desktop identity-switch scrim — the web VauSwitchButton's own scrim
              is in the hidden header, so a native-menu switch would look frozen
              without this. */}
          {isSwitchingIdentity && <IdentitySwitchScrim message={switchingMessage} />}
          {/* App-wide status bar: spans the full width above the left navbar and
              the content stage, so "acting as a VAU" is visible everywhere. */}
          <AssumedVAUBanner />
          <Box
            className={baseLayoutStyles}
            // CSS variable consumed by baseLayoutStyles: the docked Tracker reserves
            // its width at the row level so the content stage (and its portaled page
            // sidebars) AND the in-flow Notifications tray all sit to the left of the
            // Tracker instead of underneath it.
            style={{ '--stickyboard-content-margin': `${contentMarginRight}px` } as CSSProperties}
          >
            <LeftNavbar />
            <Box ref={stageRef} className={stageStyles}>
              <Box className={scrollAreaStyles}>
                <Outlet />
              </Box>
            </Box>
            <RightSidebarHost />
            <OpalChatOverlay />
            <SpotlightGlobalCommands />
            <Spotlight />
            <StickyboardDrawer
              stageLeft={stageRect.left}
              stageTop={stageRect.top}
              stageHeight={stageRect.height}
            />
          </Box>
        </Box>
      </SidebarSlotContext.Provider>
    </SpotlightProvider>
  );
}
